import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Link } from 'react-router-dom'
import { Newsletter } from './newsletter'
import { SectionDivider } from './section-divider'
import { useUser } from '@clerk/clerk-react'
import { DemoModal } from './demo-modal'
import { DebateGrid } from './debate-grid'
import { NavBar } from '@/components/nav-bar'
import { Footer } from '@/components/Footer'
import './styles.sass'
import { InfoSection } from './InfoSection'
import { PricingSection } from './PricingSection'
import { useAuth } from '@/contexts/AuthProvider'
import { AnimatedText } from '@/components/AnimatedText'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu'
import { ChevronDown } from 'lucide-react'

export default function MainPage() {
  const { isLoaded, isSignedIn, user } = useUser()
  const [demoOpen, setDemoOpen] = useState(false)
  const [videoSrc, setVideoSrc] = useState<string | undefined>(undefined)
  const { currentOrg, appUserLoaded } = useAuth()
  // logging removed

  const handleTryBtn = () => {
    if (!isLoaded) return '#'
    if (!isSignedIn) return 'sign-in?redirect=create'
    return '/create-debate'
  }

  const handleSignUpBtn = () => {
    if (!isLoaded) return '#'
    if (!isSignedIn) return 'sign-in?redirect=pricing'
    // Signed in but org not ready yet (fetch still in-flight): avoid routing glitches.
    if (!appUserLoaded) return '#'
    if (!currentOrg) return '/paddle-checkout?plan=free&cycle=monthly'
    return '/create-debate'
  }

  // Avoid flashing the "Sign up" CTA while signed-in but our org fetch is still hydrating.
  const showSignUp = isLoaded && !currentOrg && (!isSignedIn || appUserLoaded)

  const animatedPhrases = [
    'Resolved: Online debate is due for an upgrade.',
    'DB8AI.com: The AI Powered Debate Collaboration Platform',
    'Learn, Teach, Compete, and Host Tournaments',
  ]
  return (
    <div className="relative min-h-screen overflow-x-hidden noScrollbar">
      {/* GLOBAL BACKGROUND */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        <div className="absolute inset-0 bg-background" />
        <div className="absolute inset-0 animate-[hue-rotate_30s_linear_infinite] opacity-80">
          <div className="hidden md:block">
            <div className="lobby-header-blob-sm-desktop-light dark:hidden"></div>
            <div className="hidden dark:block lobby-header-blob-sm-desktop"></div>
          </div>
          <div className="hidden md:block">
            <div className="lobby-header-blob-desktop-light dark:hidden"></div>
            <div className="hidden dark:block lobby-header-blob-desktop"></div>
          </div>
          <div className="block md:hidden">
            <div className="lobby-header-blob-mobile-light dark:hidden"></div>
            <div className="hidden dark:block lobby-header-blob-mobile"></div>
          </div>
        </div>
      </div>

      {/* FOREGROUND APP */}
      <div className="relative isolate z-10">
        <NavBar />
        <DemoModal isOpen={demoOpen} setIsOpen={setDemoOpen} embedSrc={videoSrc} />

        <div className="flex flex-col space-y-5 p-5 gap-6">
          <header className="relative lobby-header flex flex-col items-center justify-center min-h-[80vh]">
            {/* Animated Text */}
            <div className="w-full max-w-4xl mx-auto px-4 z-10">
              <AnimatedText phrases={animatedPhrases} />
            </div>

            {/* Main content */}
            <div className="flex flex-col-reverse md:flex-row md:justify-around items-center w-full gap-8 px-4 md:px-12 lg:px-36 z-10">
              {/* Text Content */}
              <div className="text-center md:text-left flex flex-col gap-4">
                <div className="flex flex-row items-center justify-center md:justify-start gap-4">
                  <a href="https://debatefire.com" target="_blank" rel="noopener noreferrer">
                    <img
                      className="rounded-full w-16 sm:w-20 lg:w-32 max-w-full h-auto"
                      src="/debatefire-logo-clear-bg.webp"
                      alt="DebateFire Logo"
                    />
                  </a>
                  <div className="flex flex-col">
                    <span className="text-base font-medium">Presents:</span>
                    <div className="relative">
                      <h1 className="text-4xl sm:text-6xl lg:text-7xl font-bold leading-tight">
                        DB8AI.com
                      </h1>
                      <div className="absolute top-1/2 -translate-y-1/2 right-0 translate-x-full ml-2">
                        <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded border-2 border-red-700 shadow-lg rotate-12 inline-block">
                          PREVIEW
                        </span>
                      </div>
                    </div>
                    <small className="text-sm text-gray-500">Pronounced: "Debate AI"</small>
                  </div>
                </div>
                <h2 className="text-xl sm:text-2xl font-semibold">
                  The AI Powered Debate Collaboration Platform
                </h2>
                <h3 className="text-md max-w-xl mx-auto md:mx-0">
                  DB8AI.com gives students, coaches, and judges a fully equipped virtual debate
                  hall—designed to train, compete, and host tournaments with powerful AI support.
                </h3>
                <div className="flex flex-col sm:flex-row justify-center md:justify-start gap-4 sm:gap-6 mt-8">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="marketing"
                        className="bg-slate-700 hover:bg-slate-800 dark:bg-slate-600 dark:hover:bg-slate-700 cursor-pointer"
                      >
                        <span className="relative z-10 flex items-center gap-2">
                          Watch Video <ChevronDown size={16} />
                        </span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="z-50 min-w-[12rem] rounded-md border p-1 shadow-md bg-gray-200 dark:bg-gray-800">
                      <DropdownMenuItem
                        className="text-center cursor-pointer w-full flex justify-center hover:bg-accent"
                        onSelect={() => {
                          setVideoSrc('https://www.youtube.com/embed/Os1mi8VUWsg?autoplay=1')
                          setDemoOpen(true)
                        }}
                      >
                        Teaser
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="text-center cursor-pointer w-full flex justify-center hover:bg-accent"
                        onSelect={() => {
                          setVideoSrc('https://www.youtube.com/embed/Ykcnxw9gzjw?autoplay=1')
                          setDemoOpen(true)
                        }}
                      >
                        Walk Through
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <Link to={handleTryBtn()}>
                    <Button
                      variant="marketing"
                      className="bg-violet-700 hover:bg-violet-800 dark:bg-violet-600 dark:hover:bg-violet-700 cursor-pointer"
                      disabled={!isLoaded}
                    >
                      <span className="relative z-10">Try it</span>
                    </Button>
                  </Link>
                  {showSignUp && (
                    <Link to={handleSignUpBtn()}>
                      <Button
                        variant="marketing"
                        className="bg-teal-700 hover:bg-teal-800 dark:bg-teal-600 dark:hover:bg-teal-700 cursor-pointer"
                        onClick={() => {}}
                        disabled={!isLoaded || (isSignedIn && !appUserLoaded)}
                      >
                        <span className="relative z-10">Sign up</span>
                      </Button>
                    </Link>
                  )}
                </div>

                {/* Social (top-area link; keep subtle; don't compete with primary CTAs) */}
                <div className="mt-4 flex justify-center md:justify-start">
                  <a
                    href="https://x.com/DebateFire_"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                    title="Follow @DebateFire_ on X"
                  >
                    <span className="font-semibold">X</span>
                    <span>@DebateFire_</span>
                  </a>
                </div>
              </div>

              {/* Logo on the right */}
              <div className="flex justify-center md:justify-end">
                <div className="relative inline-block">
                  <img
                    className="rounded-full shadow-md shadow-black border border-yellow-300 border-2 w-56 sm:w-72 lg:w-96 max-w-full h-auto"
                    src="/db8ai-logo.webp"
                    alt="Logo"
                  />
                  <a href="https://debatefire.com" target="_blank" rel="noopener noreferrer">
                    <img
                      className="absolute left-0 bottom-0 rounded-full w-20 sm:w-24 lg:w-40 border border-2 border-yellow-300 shadow-md"
                      src="/debatefire-logo-clear-bg.webp"
                      alt="DebateFire Logo"
                      style={{
                        backgroundColor: 'hsl(220, 25%, 16%)',
                        transform: 'translate(-15%, 15%)',
                      }}
                    />
                  </a>
                </div>
              </div>
            </div>
          </header>

          <InfoSection
            title="What is DB8AI.com?"
            subtitle="Your ultimate virtual debate arena"
            body={
              <div className="flex flex-col space-y-4">
                <p>
                  DB8AI.com offers a unique space where humans and AI collaborate and compete in
                  structured debate formats. Whether you're preparing for tournaments, running a
                  classroom, or just practicing with friends — it's all here.
                </p>
                <h4 className="font-bold">Features:</h4>
                <ul className="list-disc pl-5 space-y-2">
                  <li>Real-time video conferencing</li>
                  <li>Evidence management</li>
                  <li>Speaker management (sequencing, timers etc.)</li>
                  <li>Speech transcription</li>
                  <li>AI flow notes</li>
                  <li>Manual flow notes tool</li>
                  <li>AI opponents / teammates</li>
                  <li>AI judges</li>
                  <li>AI recommendations for improvement</li>
                  <li>Judge ballots</li>
                  <li>Spectator voting</li>
                  <li>Text chat</li>
                  <li>Full data export (flows, transcripts, tab notes, evidence, ballots)</li>
                </ul>
              </div>
            }
            imageSrc="/screenshots/lobby"
          />

          <section className="flex flex-col items-center w-full mt-16">
            <SectionDivider title="Explore Active Debates" />
            <div className="md:px-12 lg:px-36 w-full">
              <DebateGrid />
            </div>
          </section>

          <InfoSection
            reverse
            title="Submit Evidence Seamlessly"
            subtitle="Bring your arguments with proof"
            body={
              <p>
                Upload documents, studies, and media during your debate to support your claims.
                Evidence is visible to all participants and can be used during speeches, crossfire,
                and judge review.
              </p>
            }
            imageSrc="/screenshots/evidence"
          />

          <InfoSection
            title="Track Arguments with AI Flow Notes"
            subtitle="Never lose the thread of the debate"
            body={
              <p>
                Our AI generates structured flow notes in real time, helping you follow the
                development of arguments, responses, and counterpoints across every speech. Great
                for judges, spectators, and debaters alike. The flow tool can be used to create your
                own flow notes easy!
              </p>
            }
            imageSrc="/screenshots/flownotes"
          />

          <InfoSection
            reverse
            title="Train with AI-Powered Teammates & Opponents"
            subtitle="Sharpen your skills before going live"
            body={
              <p>
                Jump into debates with realistic AI opponents or supportive AI teammates. From
                beginner-friendly practice to high-level sparring, DB8AI.com bots are fine tuned to
                speak like a real academic debater. Arguments are made in an explicit fashion to
                make learning debate a breeze!
              </p>
            }
            imageSrc="/screenshots/lobby-ai-bots-info-section"
          />

          <InfoSection
            title="Full Data Exports"
            subtitle="Capture and review everything"
            body={
              <p>
                Record your sessions for review and export all debate data for analysis, learning,
                or archiving.
              </p>
            }
            imageSrc="/screenshots/lobby-export-section"
          />

          <InfoSection
            reverse
            title="Interactive Debate Guidebook"
            subtitle="Never feel lost in a debate again"
            body={
              <p>
                Our in-app guidebook walks you through formats, speaking roles, and strategic tips —
                all context-aware and available mid-debate. It's like having a coach by your side
                every step of the way.
              </p>
            }
            imageSrc="/screenshots/guidebook"
          />

          <SectionDivider
            title="Our Pricing Plans"
            subtitle={'Choose the plan that best fits your needs'}
          />

          <PricingSection />
          <div className="font-bold text-center">
            We proudly support the debate community through contributions to the National Speech &
            Debate Association (NSDA) and the Cross Examination Debate Association (CEDA).
          </div>
          <Footer />
        </div>
      </div>
    </div>
  )
}
